import { useAuth } from '@/hooks';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import {
  Avatar,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Button,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { KText } from '@/ui-library/components';
import { SUPPORT_HOME_URL } from '@/constants/app-constants';
import { useKFlags } from '@/hooks/use-k-flags';

interface CurrentUser {
  first_name?: string;
  last_name?: string;
  avatar?: string;
}

interface UseAuthReturn {
  currUser: CurrentUser | null;
  logout: () => Promise<void>;
}

export const UserProfile = () => {
  const { currUser: user, logout } = useAuth() as UseAuthReturn;
  const history = useHistory();
  // usePermissionGuard spreads dynamic permission keys into its return object;
  // TS can only see the literal `permissions` field, so cast to a Record for
  // the dynamic flags we read.
  const guard = usePermissionGuard() as Record<string, boolean> & {
    permissions: unknown;
  };
  const isAccountInviteEnabled = Boolean(guard.isAccountInviteEnabled);
  const { ats_enabled, feNotificationCenter } = useKFlags();

  const onLogout = async () => {
    await logout();
  };

  const renderTrigger = () => (
    <Avatar
      name={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()}
      src={user?.avatar}
      width="24px"
      height="24px"
      className="avatar-md"
    />
  );

  return (
    <Menu>
      {({ onClose }) => (
        <>
          <MenuButton
            as={Button}
            p="0px !important"
            minW="24px"
            h="24px"
            border="none"
            bg="none"
            _hover={{ bg: 'none' }}
            _active={{ bg: 'none' }}
            data-test-id="user-profile"
          >
            {renderTrigger()}
          </MenuButton>
          <MenuList
            p="0px !important"
            width="220px"
            borderRadius="md"
            overflow="hidden"
          >
            {ats_enabled && isAccountInviteEnabled && (
              <Flex
                data-test-id="profile-settings"
                px="md"
                py="sm"
                cursor="pointer"
                onClick={() => {
                  history.push('/settings/profile-settings');
                  onClose();
                }}
                borderBottom="1px solid"
                borderColor="border-color"
                _hover={{ bg: 'secondary-25' }}
                transition="background-color 0.2s"
              >
                <KText size="sm-medium" color="secondary-900">
                  Profile settings
                </KText>
              </Flex>
            )}
            {!ats_enabled && isAccountInviteEnabled && (
              <Flex
                data-test-id="invite-teammates"
                px="md"
                py="sm"
                cursor="pointer"
                onClick={() => {
                  history.push('/settings/users-roles');
                  onClose();
                }}
                borderBottom="1px solid"
                borderColor="border-color"
                _hover={{ bg: 'secondary-25' }}
                transition="background-color 0.2s"
              >
                <KText size="sm-medium" color="secondary-900">
                  Invite teammates
                </KText>
              </Flex>
            )}
            {feNotificationCenter && (
              <Flex
                data-test-id="notifications-center"
                px="md"
                py="sm"
                cursor="pointer"
                onClick={() => {
                  history.push('/settings/notifications-center');
                  onClose();
                }}
                borderBottom="1px solid"
                borderColor="border-color"
                _hover={{ bg: 'secondary-25' }}
                transition="background-color 0.2s"
              >
                <KText size="sm-medium" color="secondary-900">
                  Notifications center
                </KText>
              </Flex>
            )}
            <Flex
              data-test-id="help-support"
              px="md"
              py="sm"
              cursor="pointer"
              onClick={() => {
                window.open(`${SUPPORT_HOME_URL}`, '_blank');
                onClose();
              }}
              borderBottom="1px solid"
              borderColor="border-color"
              _hover={{ bg: 'secondary-25' }}
              transition="background-color 0.2s"
            >
              <KText size="sm-medium" color="secondary-900">
                Help & Support
              </KText>
            </Flex>
            <Flex
              data-test-id="logout"
              px="md"
              py="sm"
              cursor="pointer"
              onClick={() => {
                onLogout();
                onClose();
              }}
              _hover={{ bg: 'secondary-25' }}
              transition="background-color 0.2s"
            >
              <KText size="sm-medium" color="secondary-900">
                Logout
              </KText>
            </Flex>
          </MenuList>
        </>
      )}
    </Menu>
  );
};
